import { createApp } from 'vue';
import Loading from './loading.vue';

const mountedInstances = new WeakMap();

const defaultProps = {
    fullscreen: false,
    background: '',
    opacity: 1,
    color: 'brand-primary',
    size: 16,
    text: '',
    textColor: 'fixed-primary',
};

const loadingPropKeys = Object.keys(defaultProps);

function kebabCase(str) {
    return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

function extractPropsFromAttrs(el, fallbackProps = {}) {
    const props = { ...fallbackProps };

    loadingPropKeys.forEach((key) => {
        if (props[key] !== undefined) return;

        const attrValue = el.getAttribute(`el-loading-${kebabCase(key)}`);
        if (attrValue !== null) {
            const defaultVal = defaultProps[key];
            if (typeof defaultVal === 'boolean') {
                props[key] = attrValue === 'true';
            } else if (typeof defaultVal === 'number') {
                props[key] = Number(attrValue);
            } else {
                props[key] = attrValue;
            }
        }
    });

    return props;
}

export default {
    mounted(el, binding) {
        const { value } = binding;
        const active = value === true || (typeof value === 'object' && value.loading);
        if (!active) return;

        const rawProps = typeof value === 'object' ? { ...value } : {};
        if ('loading' in rawProps) delete rawProps.loading;

        const props = extractPropsFromAttrs(el, rawProps);

        const app = createApp(Loading, props);
        const instance = app.mount(document.createElement('div'));

        if (props.fullscreen) {
            document.body.appendChild(instance.$el);
        } else {
            // eslint-disable-next-line no-param-reassign
            el.style.position = 'relative';
            el.appendChild(instance.$el);
        }

        mountedInstances.set(el, { app, el: instance.$el });
    },

    updated(el, binding) {
        const prev = mountedInstances.get(el);
        const isVisible = binding.value === true || binding.value?.loading;

        if (!isVisible && prev) {
            prev.app.unmount();
            prev.el.remove();
            mountedInstances.delete(el);
            return;
        }

        if (isVisible) {
        // Unmount old instance if exists
            if (prev) {
                prev.app.unmount();
                prev.el.remove();
                mountedInstances.delete(el);
            }

            // Re-extract props on update
            const props = extractPropsFromAttrs(el, typeof binding.value === 'object' ? { ...binding.value } : {});
            if ('loading' in props) delete props.loading;

            const app = createApp(Loading, props);
            const instance = app.mount(document.createElement('div'));

            if (props.fullscreen) {
                document.body.appendChild(instance.$el);
            } else {
                // eslint-disable-next-line no-param-reassign
                el.style.position = 'relative';
                el.appendChild(instance.$el);
            }

            mountedInstances.set(el, { app, el: instance.$el });
        }
    },

    unmounted(el) {
        const prev = mountedInstances.get(el);
        if (prev) {
            prev.app.unmount();
            prev.el.remove();
            mountedInstances.delete(el);
        }
    },
};
